/**
 * Full-label (A5) match helpers over the rendered DOM. A match is a "head"
 * mark (not a `search-highlight-cont` continuation piece); it belongs to the
 * innermost row whose body contains it, so headings (outside bodies) and
 * excluded rows (Highlights) never count.
 */
export interface ArticleRowConfig {
  rowSelector: string;
  bodySelector: string;
  rowKey(row: Element): string | null;
  excluded?(row: Element): boolean;
}

export const US_ROWS: ArticleRowConfig = {
  rowSelector: '[data-label-row]',
  bodySelector: '[data-label-body]',
  rowKey: (row) => row.getAttribute('data-label-row'),
  excluded: (row) => row.hasAttribute('data-label-highlights'),
};

export const JP_ROWS: ArticleRowConfig = {
  rowSelector: '[data-section]',
  bodySelector: '.jp-content',
  rowKey: (row) => row.getAttribute('data-section'),
};

export const HEAD_SELECTOR = 'mark.search-highlight:not(.search-highlight-cont)';

function rowOfHead(head: Element, cfg: ArticleRowConfig): { row: Element; key: string } | null {
  const body = head.closest(cfg.bodySelector);
  const row = body?.closest(cfg.rowSelector);
  if (!row || cfg.excluded?.(row)) return null;
  const key = cfg.rowKey(row);
  return key === null ? null : { row, key };
}

/** Heads inside a counted row's body, in document order. */
export function collectHeads(root: Element, cfg: ArticleRowConfig): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(HEAD_SELECTOR)).filter((h) => rowOfHead(h, cfg));
}

/** Per-row head counts (mark -> closest body -> closest row; excluded rows skipped). */
export function countByRow(root: Element, cfg: ArticleRowConfig): Map<string, number> {
  const counts = new Map<string, number>();
  for (const h of collectHeads(root, cfg)) {
    const key = rowOfHead(h, cfg)!.key;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return counts;
}

/** The head plus its following .search-highlight-cont marks (document order, until the next head). */
export function marksOfMatch(head: HTMLElement): HTMLElement[] {
  const root = head.getRootNode() as ParentNode;
  const all = Array.from(root.querySelectorAll<HTMLElement>('mark.search-highlight'));
  const out = [head];
  for (let i = all.indexOf(head) + 1; i > 0 && i < all.length; i++) {
    if (!all[i]!.classList.contains('search-highlight-cont')) break;
    out.push(all[i]!);
  }
  return out;
}

/** Matches-only keep set: rows with count >= 1, plus every row r with
 *  n === r || n.startsWith(r + '.') for a matching row n (prefix rule). */
export function matchesOnlyKeep(
  rows: { key: string; sectionNumber: string | null }[],
  counts: Map<string, number>,
): Set<string> {
  const matching = rows.filter((r) => (counts.get(r.key) ?? 0) >= 1);
  const keep = new Set<string>();
  for (const r of rows) {
    const rn = r.sectionNumber;
    if (
      (counts.get(r.key) ?? 0) >= 1 ||
      (rn && matching.some((m) => m.sectionNumber && (m.sectionNumber === rn || m.sectionNumber.startsWith(rn + '.'))))
    ) {
      keep.add(r.key);
    }
  }
  return keep;
}
